/**
 * Pure helpers for the analytics dot plot. Ported from the reference renderer
 * `optr-agent-core/scripts/analytics/value-events-dotplot.ts` so the console
 * grid draws dots identically. Kept side-effect free for unit testing.
 */

import type { DotplotCell, DotplotUser, CatalogCategory } from '@/types/analytics';

/** Dot diameter buckets by daily event count (matches the reference). */
export function dotSizePx(total: number): number {
  if (total >= 50) return 14;
  if (total >= 10) return 11;
  return 8;
}

/** byEvent key carrying the per-day automation message count. */
export const AUTOMATION_EVENT = 'message.added:automation';

/**
 * A day with no human engagement but some automation-generated volume — the
 * cells the grids would otherwise render blank. These get the hollow marker.
 */
export function isAutomationOnly(cell: DotplotCell): boolean {
  return cell.total === 0 && (cell.automation ?? 0) > 0;
}

/**
 * Dominant category = the one with the most events that day. Ties resolve to
 * the earliest category in catalog order, so `categoryKeys` must be passed in
 * catalog order.
 */
export function dominantCategory(
  byCategory: Record<string, number>,
  categoryKeys: string[],
): string | undefined {
  let best: string | undefined;
  let bestCount = 0;
  for (const key of categoryKeys) {
    const n = byCategory[key] ?? 0;
    if (n > bestCount) {
      best = key;
      bestCount = n;
    }
  }
  return best;
}

/** UTC day-of-week check — weekends get the column tint. */
export function isWeekendUTC(day: string): boolean {
  const d = new Date(`${day}T00:00:00Z`).getUTCDay();
  return d === 0 || d === 6;
}

/** Short column header, e.g. `2026-06-14` → `6/14` (UTC). */
export function formatDayLabel(day: string): string {
  const dt = new Date(`${day}T00:00:00Z`);
  return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}`;
}

/** Best available display name: full name → email → truncated userId. */
export function userDisplayName(user: DotplotUser): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (name) return name;
  if (user.email) return user.email;
  return `${user.userId.slice(0, 14)}…`;
}

/** Two-letter avatar fallback initials. */
export function userInitials(user: DotplotUser): string {
  const first = user.firstName?.[0] ?? '';
  const last = user.lastName?.[0] ?? '';
  const initials = `${first}${last}`.trim();
  if (initials) return initials.toUpperCase();
  if (user.email) return user.email[0]?.toUpperCase() ?? '?';
  return '#';
}

/** Pick the catalog color for the active theme. */
export function categoryColor(
  category: CatalogCategory | undefined,
  theme: 'light' | 'dark',
): string | undefined {
  return category?.color?.[theme];
}

/**
 * Per-user category totals over the whole window — powers the accessible
 * table-view fallback and export.
 */
export function userCategoryTotals(
  user: DotplotUser,
  categoryKeys: string[],
): { totals: Record<string, number>; sum: number } {
  const totals: Record<string, number> = {};
  let sum = 0;
  for (const cell of Object.values(user.cells)) {
    for (const key of categoryKeys) {
      const n = cell.byCategory?.[key] ?? 0;
      if (n) {
        totals[key] = (totals[key] ?? 0) + n;
        sum += n;
      }
    }
  }
  return { totals, sum };
}

/** byEvent breakdown sorted desc, for tooltips. */
export function sortedEventBreakdown(cell: DotplotCell): [string, number][] {
  return Object.entries(cell.byEvent ?? {}).sort((a, b) => b[1] - a[1]);
}

/** Total automation-generated messages for a user over the window. */
export function userAutomationTotal(user: DotplotUser): number {
  let sum = 0;
  for (const cell of Object.values(user.cells)) {
    sum += cell.automation ?? 0;
  }
  return sum;
}
